import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import {
  User,
  onAuthStateChanged,
  signOut,
  signInAnonymously,
  getIdToken
} from 'firebase/auth';
import { auth } from '@/lib/firebase-config';
import {
  getScopedDb,
  updateScopedDb,
  getUserContext,
  ScopedDatabase,
  UserContext
} from '@/components/authnz/lib/scoped-db';
import { getCachedRedirectResult, clearRedirectResult } from '@/lib/auth-redirect-handler';
import { completeLogin } from '@/lib/access/login-client';
import { clearPendingPurchasePriceId } from '@/lib/purchase-handoff-client';
import type { DecisionJSON } from '@/lib/access/types';
import { setUserInfo as setDebugUserInfo } from '@/lib/activity-logger';

interface ServerUser {
  userId: string;
  email: string;
  displayName: string;
  photoURL: string;
  isGuest: boolean;
  roles?: string[]; // Roles from server-side session (optional, for session cookie auth)
}
import logger, { getCorrelationId } from '@/lib/logging';
import { apiFetch, prefetchCsrfToken, clearCsrfTokenCache, csrfMutatingFetch } from '@/lib/api-client';
import { decisionJsonFromSession, describeSessionEnded, fetchSessionStatus, isMissingSessionStatus, normalizeSessionResponse } from '@/lib/session-fetch';
import { remintSessionIfExpiresSoon } from '@/lib/session-remint';
import {
  isInvalidCredentialFailure,
  recoverExpiredSession,
  registerSessionRecovery,
  requestSessionRecovery,
} from '@/lib/session-recovery';
import { rememberStaleSessionOnLogin } from '@/lib/login-user-messages';

interface AuthContextType {
  user: User | null;
  serverUser: ServerUser | null;
  decisionJson: DecisionJSON | null;
  loading: boolean;
  logout: (options?: { global?: boolean }) => Promise<void>;

  initializeAuth: () => void;
  isGuest: boolean;
  userId: string | null;
  userContext: UserContext | null;
  scopedDb: ScopedDatabase | null;
  isInitialized: boolean;
  isAuthenticated: boolean;
  refreshServerSession: () => Promise<ServerUser | null>;
}

/** Exported for optional-hooks.ts — prefer useAuth() in normal usage. */
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [serverUser, setServerUser] = useState<ServerUser | null>(null);
  const [decisionJson, setDecisionJson] = useState<DecisionJSON | null>(null);
  const [loading, setLoading] = useState(true); // Start with loading true
  const [userContext, setUserContext] = useState<UserContext | null>(null);
  const [scopedDb, setScopedDb] = useState<ScopedDatabase | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Track if Firebase auth has been initialized (first onAuthStateChanged callback has fired)
  const firebaseAuthInitializedRef = useRef(false);
  // Track if server session check has completed
  const serverSessionCheckCompletedRef = useRef(false);
  // Track previous user UID to detect changes
  const lastKnownUidRef = useRef<string | null>(null);

  // Handle OAuth redirect result (already captured by auth-redirect-handler)
  useEffect(() => {
    const handleOAuthRedirect = async () => {
      try {
        logger.debug('🔍 [AuthContext] Checking for cached OAuth redirect result...');
        const result = getCachedRedirectResult();

        if (result && result.user) {
          logger.debug('✅ [AuthContext] OAuth redirect found (cached), user:', result.user.uid);

          // Check if session was already created
          // We always attempt this, even on web.app domains, as we use App Hosting
          const sessionCheckResponse = await fetchSessionStatus();

          if (sessionCheckResponse.ok) {
            const sessionData = await sessionCheckResponse.json();

            if (sessionData.authenticated && sessionData.user) {
              logger.debug('✅ [AuthContext] Session already exists from auth-redirect-handler');
              setServerUser(sessionData.user);
              setDecisionJson(decisionJsonFromSession(sessionData));
              setUser(result.user);
              setIsInitialized(true);

              // Update user context and scoped database
              const context = getUserContext(result.user);
              setUserContext(context);
              const db = updateScopedDb(result.user);
              setScopedDb(db);

              // CRITICAL: Set loading to false so LoginPage can redirect
              setLoading(false);

              // Clear the cached result since we've handled it
              clearRedirectResult();
              return;
            }
          }

          // Session doesn't exist yet, create it
          logger.debug(`📡 [AuthContext] Creating server session for user: ${result.user.email} (${result.user.uid})`);
          setDebugUserInfo(result.user.email, result.user.uid);
          const idToken = await getIdToken(result.user);

          const response = await completeLogin(idToken);

          if (response.ok) {
            // Capture correlation ID from server response only if we don't already have a valid one
            const correlationId = response.headers.get('x-correlation-id');
            if (correlationId) {
              try {
                const { setCorrelationId, hasServerCorrelationId } = await import('@/lib/logging/correlation-id');
                if (!hasServerCorrelationId()) {
                  setCorrelationId(correlationId);
                  logger.debug(`🔗 [AuthContext] Updated correlation ID from server (OAuth): ${correlationId}`);
                }
              } catch (e) { /* ignore */ }
            }

            const data = await response.json();
            logger.debug('🔍 [AuthContext] Raw /login response parsed (OAuth):', JSON.stringify(data, null, 2));
            logger.debug('✅ [AuthContext] Session created:', data.user);
            setServerUser(data.user);
            setDecisionJson(decisionJsonFromSession(data));
            setUser(result.user);
            setIsInitialized(true);

            // Update user context and scoped database
            const context = getUserContext(result.user);
            setUserContext(context);
            const db = updateScopedDb(result.user);
            setScopedDb(db);

            // CRITICAL: Set loading to false so LoginPage can redirect
            setLoading(false);
          } else if (response.status === 403) {
            const data = await response.json().catch(() => ({}));
            const { classifyLogin403Body, rememberStaleSessionOnLogin } = await import(
              '@/lib/login-user-messages'
            );
            const action = classifyLogin403Body(data);
            if (action.kind === 'stay-on-login') {
              logger.warn(
                '🚫 [AuthContext] Login needs a fresh sign-in (stale auth_time); staying on /login'
              );
              rememberStaleSessionOnLogin();
              if (auth) {
                await signOut(auth);
              }
              setLoading(false);
            } else {
              const redirectTo = action.path;
              logger.warn(
                `🚫 [AuthContext] Login denied (403): ${data.message || 'Not invited'}, redirecting to ${redirectTo}`
              );
              setLoading(false);
              if (typeof window !== 'undefined' && window.location.pathname !== redirectTo) {
                window.location.assign(redirectTo);
              }
            }
          } else {
            logger.error('❌ [AuthContext] Failed to create session:', await response.text());
            setLoading(false);
          }

          // Clear the cached result since we've handled it
          clearRedirectResult();
        } else if (result && result.error === 'redirect_failed') {
          logger.error('❌ [AuthContext] OAuth redirect failed:', result.message);
          // Show error message to user (this will be picked up by LoginPage)
          setLoading(false);
        } else {
          logger.debug('ℹ️ [AuthContext] No OAuth redirect cached');
          // Don't set loading to false here - let the session check handle it
        }
      } catch (error) {
        logger.error('❌ [AuthContext] OAuth redirect error:', error);
        setLoading(false); // Ensure loading is set to false on error
      }
    };

    handleOAuthRedirect();
  }, []);

  // Track generation of session checks to prevent race conditions
  const sessionCheckGenerationRef = useRef(0);
  const previousRolesRef = useRef<string[] | null>(null);

  const applySessionPayload = useCallback((data: Record<string, unknown>) => {
    const session = normalizeSessionResponse(data);
    if (session.authenticated && session.user) {
      setServerUser(session.user as typeof serverUser);
      setDecisionJson(session.decisionJson);
      setIsInitialized(true);
    }
    return session;
  }, []);

  const maybeRemintFromExpiresSoon = useCallback(
    async (data: Record<string, unknown>, firebaseUser: User | null): Promise<void> => {
      if (!firebaseUser || !data.expiresSoon) {
        return;
      }

      const outcome = await remintSessionIfExpiresSoon({
        expiresSoon: Boolean(data.expiresSoon),
        getIdToken: (forceRefresh) => getIdToken(firebaseUser, forceRefresh),
        completeLogin,
      });

      if (outcome !== 'reminted') {
        return;
      }

      const refresh = await fetchSessionStatus();
      if (!refresh.ok) {
        return;
      }

      const refreshed = await refresh.json();
      applySessionPayload(refreshed);
    },
    [applySessionPayload],
  );

  // Helper function to set loading to false only after Firebase auth has initialized
  const trySetLoadingFalse = () => {
    if (firebaseAuthInitializedRef.current && serverSessionCheckCompletedRef.current) {
      logger.debug('✅ [AuthContext] Both Firebase auth and server session check completed, setting loading to false');
      setLoading(false);
    } else {
      logger.debug('⏳ [AuthContext] Waiting for initialization:', {
        firebaseAuthInitialized: firebaseAuthInitializedRef.current,
        serverSessionCheckCompleted: serverSessionCheckCompletedRef.current
      });
    }
  };

  // Set up Firebase auth state listener - this is CRITICAL for auth persistence
  useEffect(() => {
    logger.debug('🔍 [AuthContext] Setting up Firebase auth state listener...');

    if (!auth) {
      logger.warn('[AuthContext] Auth instance is null, skipping listener setup');
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      // Check if user has actually changed to avoid unnecessary loading states (e.g. token refresh)
      // We use a ref because we can't access state inside this closure easily without rebuilding the listener
      const previousUid = lastKnownUidRef.current;
      const currentUid = currentUser?.uid;
      const userChanged = previousUid !== currentUid;

      logger.debug('[AuthContext] Firebase auth state changed:', {
        hasUser: !!currentUser,
        uid: currentUid,
        email: currentUser?.email,
        userChanged,
        previousUid
      });

      lastKnownUidRef.current = currentUid || null;

      // Mark Firebase auth as initialized (first callback has fired)
      if (!firebaseAuthInitializedRef.current) {
        firebaseAuthInitializedRef.current = true;
        logger.debug('✅ [AuthContext] Firebase auth initialized (first onAuthStateChanged callback)');
      }

      // CRITICAL: If the user changed, set loading to true immediately.
      // This prevents the UI from rendering in an inconsistent state (e.g. User set but ServerUser not yet synced)
      // which causes race conditions in AbilityContext.
      if (userChanged) {
        logger.debug('⏳ [AuthContext] User changed, setting loading=true to prevent race conditions', { newUid: currentUid });
        setLoading(true);
        // We also reset the server session check flag because for a new user, we need to re-verify/create session
        serverSessionCheckCompletedRef.current = false;
        // Increment generation to invalidate any pending checks
        sessionCheckGenerationRef.current++;
        logger.debug('🔄 [AuthContext] Incremented session gen to:', sessionCheckGenerationRef.current);
      }

      setUser(currentUser);

      // Set user info for debug logger
      setDebugUserInfo(currentUser?.email || null, currentUser?.uid || null);

      // Update user context and scoped database
      const context = getUserContext(currentUser);
      setUserContext(context);

      // Update the global scoped database instance
      const db = updateScopedDb(currentUser);
      setScopedDb(db);

      // If we have a Firebase user, ensure we have a server session
      if (currentUser) {
        logger.debug('🔍 [AuthContext] Firebase user detected, synchronizing server session...');
        // We do NOT call checkServerSession() separately here to avoid race conditions.
        // Instead, we call syncSessionWithUser which handles checking AND creating if needed,
        // and only THEN marks the session check as complete.
        await syncSessionWithUser(currentUser);
      } else {
        // No Firebase user
        logger.debug('ℹ️ [AuthContext] No Firebase user');
        const db = updateScopedDb(null);
        setScopedDb(db);

        // If user changed to null (logout), we should also ensure we clear server session if needed
        // But checkServerSession() handles verifying the current state.
        await checkServerSession();
      }

      // syncSessionWithUser / checkServerSession set serverSessionCheckCompletedRef in finally
      trySetLoadingFalse();
    });

    return () => unsubscribe();
  }, []); // Only run once on mount

  // Check server-side session
  const checkServerSession = async () => {
    const generation = sessionCheckGenerationRef.current;
    const correlationId = getCorrelationId();
    const hostname = typeof window !== 'undefined' ? window.location.hostname : 'unknown';
    const origin = typeof window !== 'undefined' ? window.location.origin : 'unknown';

    try {
      logger.debug(`🔍 [AuthContext] Checking server session (Gen: ${generation})...`, {
        category: 'auth',
        correlationId,
        hostname,
        origin,
        url: '/api/auth/session',
      });

      // Import routing debug logger
      const { logCookieInfo } = await import('@/lib/routing-debug');

      // Check for cookies before making request
      const hasCookies = typeof document !== 'undefined' && document.cookie.includes('__session');
      logCookieInfo(
        {
          present: hasCookies,
          name: hasCookies ? '__session' : undefined,
        },
        {
          hostname,
          origin,
          path: '/api/auth/session',
        }
      );

      const response = await fetchSessionStatus();

      // If generation changed, abort processing results as they are stale
      if (sessionCheckGenerationRef.current !== generation) {
        logger.debug(`🚫 [AuthContext] Session check aborted (Gen mismatch: ${generation} vs ${sessionCheckGenerationRef.current})`, {
          category: 'auth',
          correlationId,
        });
        return;
      }

      logger.debug(`📥 [AuthContext] Session check response received`, {
        category: 'auth',
        correlationId,
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        hostname,
        origin,
      });

      if (response.ok) {
        // Capture correlation ID from server response only if we don't already have a valid one
        const serverCorrelationId = response.headers.get('x-correlation-id');
        if (serverCorrelationId) {
          try {
            const { setCorrelationId, hasServerCorrelationId } = await import('@/lib/logging/correlation-id');
            if (!hasServerCorrelationId()) {
              setCorrelationId(serverCorrelationId);
              logger.debug(`🔗 [AuthContext] Updated correlation ID from server: ${serverCorrelationId}`, {
                category: 'auth',
              });
            }
          } catch (e) {
            // Ignore import errors
          }
        }

        const data = await response.json();
        const session = normalizeSessionResponse(data);
        logger.debug('🔍 [AuthContext] Raw /session response parsed:', {
          category: 'auth',
          correlationId,
          authenticated: session.authenticated,
          hasUser: !!session.user,
          userId: (session.user as { userId?: string })?.userId,
          email: (session.user as { email?: string })?.email,
          roles: (session.user as { roles?: string[] })?.roles,
          rolesCount: (session.user as { roles?: string[] })?.roles?.length || 0,
        });

        if (session.authenticated && session.user) {
          logger.debug('✅ [AuthContext] Server session found:', {
            category: 'auth',
            correlationId,
            userId: (session.user as { userId?: string }).userId,
            email: (session.user as { email?: string }).email,
            roles: (session.user as { roles?: string[] }).roles,
            hasRoles: !!((session.user as { roles?: string[] }).roles?.length),
            hostname,
            origin,
          });
          applySessionPayload(data);
          await maybeRemintFromExpiresSoon(data, user);
        } else {
          logger.debug('ℹ️ [AuthContext] No server session', {
            category: 'auth',
            correlationId,
            hostname,
            origin,
            reason: data.authenticated ? 'no user data' : 'not authenticated',
          });
        }
      } else if (isMissingSessionStatus(response.status)) {
        // Cookie cleared or not minted yet. A 401 is signed-out, not an authorization failure.
        logger.debug('ℹ️ [AuthContext] No server session', {
          category: 'auth',
          correlationId,
          status: response.status,
          hostname,
          origin,
        });
      } else {
        logger.warn(`⚠️ [AuthContext] Session check failed: ${response.status} ${response.statusText}`, {
          category: 'auth',
          correlationId,
          status: response.status,
          statusText: response.statusText,
          hostname,
          origin,
        });

        // Log authorization failure
        const { logAuthorizationFailure } = await import('@/lib/routing-debug');
        logAuthorizationFailure(
          'authorization_failed',
          {
            requestContext: {
              hostname,
              origin,
              path: '/api/auth/session',
            },
            cookieInfo: {
              present: hasCookies,
            },
            authState: {
              authenticated: false,
            },
            additionalInfo: {
              httpStatus: response.status,
              httpStatusText: response.statusText,
            },
          }
        );
      }
    } catch (error: any) {
      logger.error('❌ [AuthContext] Error checking session:', {
        category: 'auth',
        correlationId,
        error: error.message,
        errorName: error.name,
        stack: error.stack?.substring(0, 500),
        hostname,
        origin,
      });

      // Log authorization failure
      try {
        const { logAuthorizationFailure } = await import('@/lib/routing-debug');
        logAuthorizationFailure(
          'authorization_failed',
          {
            requestContext: {
              hostname,
              origin,
              path: '/api/auth/session',
            },
            additionalInfo: {
              error: error.message,
              errorName: error.name,
            },
          }
        );
      } catch (e) {
        // Ignore import errors
      }
    } finally {
      // Only mark complete if this is still the latest generation
      if (sessionCheckGenerationRef.current === generation) {
        serverSessionCheckCompletedRef.current = true;
        trySetLoadingFalse();
      }
    }
  };

  /**
   * Synchronize the server session with the current Firebase user.
   * This checks if a session exists, and if not (or if invalid), creates one.
   * Crucially, it manages the serverSessionCheckCompletedRef to prevent premature loading completion.
   */
  const syncSessionWithUser = async (currentUser: User) => {
    // Reset checking state just in case, though usually we are in a loading state here
    serverSessionCheckCompletedRef.current = false;
    // Capture the current generation (already incremented in onAuthStateChanged if user changed)
    const generation = sessionCheckGenerationRef.current; // Don't increment here, as onAuthStateChanged handles it OR it's a re-sync
    // Actually, if syncSessionWithUser is called directly (not from onAuthStateChanged), we might want to ensure it's tracked.
    // But mostly it's called from onAuthStateChanged. Let's assume onAuthStateChanged manages the invalidation of previous checks if user changed.

    // If called from onAuthStateChanged (user changed), generation was already incremented.
    // So we just use current.

    logger.debug(`🔄 [AuthContext] Syncing session for user (Gen: ${generation})...`);

    try {
      let sessionResp: Response | null = null;
      let attempts = 0;
      const maxAttempts = 3;

      while (attempts < maxAttempts && !sessionResp) {
        // If generation changed during retry, abort
        if (sessionCheckGenerationRef.current !== generation) {
          logger.debug(`🚫 [AuthContext] Sync aborted during retry (Gen mismatch)`);
          return;
        }

        try {
          sessionResp = await fetchSessionStatus();
        } catch (fetchError) {
          attempts++;
          if (attempts >= maxAttempts) throw fetchError;
          logger.warn(`⚠️ [AuthContext] Session fetch failed, retrying (${attempts}/${maxAttempts})...`);
          await new Promise(resolve => setTimeout(resolve, 500 * attempts));
        }
      }

      if (sessionCheckGenerationRef.current !== generation) return;

      if (!sessionResp) throw new Error('Failed to fetch session after retries');

      let sessionExistsAndValid = false;

      if (sessionResp.ok) {
        const sessionData = await sessionResp.json();
        if (sessionData.authenticated && sessionData.user && sessionData.user.userId === currentUser.uid) {
          logger.debug('✅ [AuthContext] Server session verified for current user:', sessionData.user.userId);
          applySessionPayload(sessionData);
          await maybeRemintFromExpiresSoon(sessionData, currentUser);
          sessionExistsAndValid = true;
        } else if (sessionData.authenticated && sessionData.user) {
          logger.warn('⚠️ [AuthContext] Server session found for different user. Forcing logout/re-login...', {
            sessionUserId: sessionData.user.userId,
            currentUserId: currentUser.uid
          });
          // Mismatch - we'll proceed to create a new session (which initiates login)
          // The /login endpoint overwrites existing sessions usually, or we can force logout first if needed.
          // For now, let's try to overwrite by calling login.
        }
      }

      if (sessionExistsAndValid) {
        return;
      }

      // If we are here, we need to create the session
      logger.debug('📡 [AuthContext] Creating server session (sync flow)...');
      const maxLoginAttempts = 3;
      let loginResp: Response | null = null;
      let lastLoginErrorText = '';

      for (let attempt = 1; attempt <= maxLoginAttempts; attempt++) {
        if (sessionCheckGenerationRef.current !== generation) return;

        const forceRefresh = attempt > 1;
        const idToken = await getIdToken(currentUser, forceRefresh);

        if (sessionCheckGenerationRef.current !== generation) return;

        loginResp = await completeLogin(idToken);

        if (loginResp.ok || loginResp.status === 403) {
          break;
        }

        lastLoginErrorText = await loginResp.text();
        logger.warn(
          `⚠️ [AuthContext] Server login attempt ${attempt}/${maxLoginAttempts} failed:`,
          lastLoginErrorText,
        );

        if (attempt < maxLoginAttempts) {
          const retryDelayMs = loginResp.status === 503 ? 2000 * attempt : 400 * attempt;
          await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
        }
      }

      if (!loginResp) {
        throw new Error('Login request did not complete');
      }

      if (loginResp.ok) {
        // Capture correlation ID from server response only if we don't already have a valid one
        const correlationId = loginResp.headers.get('x-correlation-id');
        if (correlationId) {
          try {
            const { setCorrelationId, hasServerCorrelationId } = await import('@/lib/logging/correlation-id');
            if (!hasServerCorrelationId()) {
              setCorrelationId(correlationId);
              logger.debug(`🔗 [AuthContext] Updated correlation ID from server (Sync): ${correlationId}`);
            }
          } catch (e) { /* ignore */ }
        }

        const data = await loginResp.json();
        logger.debug('🔍 [AuthContext] Raw /login response parsed (Sync):', JSON.stringify(data, null, 2));
        logger.debug('✅ [AuthContext] Server session created (sync flow):', data.user);

        if (sessionCheckGenerationRef.current === generation) {
          logger.debug('✅ [AuthContext] Sync successful (Gen matched). User:', data.user.userId);
          setServerUser(data.user);
          setDecisionJson(decisionJsonFromSession(data));
          setIsInitialized(true);

          const context = getUserContext(currentUser);
          setUserContext(context);
          const db = updateScopedDb(currentUser);
          setScopedDb(db);

        } else {
          logger.warn('⚠️ [AuthContext] Sync finished but Gen MISMATCH. Ignored.', { generation, current: sessionCheckGenerationRef.current });
        }
      } else if (loginResp.status === 403) {
        const data = await loginResp.json().catch(() => ({}));
        const { classifyLogin403Body, rememberStaleSessionOnLogin } = await import(
          '@/lib/login-user-messages'
        );
        const action = classifyLogin403Body(data);
        if (action.kind === 'stay-on-login') {
          logger.warn(
            '🚫 [AuthContext] Login needs a fresh sign-in (stale auth_time); staying on /login'
          );
          rememberStaleSessionOnLogin();
          if (sessionCheckGenerationRef.current === generation && auth) {
            try {
              await signOut(auth);
              setServerUser(null);
              setDecisionJson(null);
              setUser(null);
              setUserContext(null);
              setScopedDb(updateScopedDb(null));
            } catch (signOutError) {
              logger.warn('[AuthContext] Sign-out after stale login mint failed:', signOutError);
            }
          }
        } else {
          const redirectTo = action.path;
          logger.warn(
            `🚫 [AuthContext] Login denied (403): ${data.message || 'Not invited'}, redirecting to ${redirectTo}`
          );
          if (typeof window !== 'undefined' && window.location.pathname !== redirectTo) {
            window.location.assign(redirectTo);
          }
        }
      } else {
        logger.error('❌ [AuthContext] Failed to create server session (sync flow):', lastLoginErrorText);
        // Cannot establish a server session — sign out so protected routes do not hang forever.
        if (sessionCheckGenerationRef.current === generation && auth) {
          try {
            await signOut(auth);
            setServerUser(null);
            setDecisionJson(null);
            setUser(null);
            setUserContext(null);
            setScopedDb(updateScopedDb(null));
          } catch (signOutError) {
            logger.warn('[AuthContext] Sign-out after failed server login failed:', signOutError);
          }
        }
      }
    } catch (error) {
      logger.error('❌ [AuthContext] Error syncing server session:', error);
    } finally {
      // ALWAYS mark complete, even on error, so the app doesn't hang on loading screen
      // BUT only if generation matches
      if (sessionCheckGenerationRef.current === generation) {
        serverSessionCheckCompletedRef.current = true;
        trySetLoadingFalse();
      } else {
        logger.debug(`🚫 [AuthContext] Sync finally skipped (Gen mismatch: ${generation} vs ${sessionCheckGenerationRef.current})`);
      }
    }
  };

  // Initial session check on mount (for direct navigation to the app)
  useEffect(() => {
    // Only check session if we don't already have a serverUser from OAuth redirect
    if (!serverUser) {
      const timer = setTimeout(() => {
        checkServerSession();
      }, 100);
      return () => clearTimeout(timer);
    } else {
      logger.debug('ℹ️ [AuthContext] Skipping initial session check - OAuth session already established');
      // For OAuth redirect, we already have the user, so mark server session check as complete
      // and try to set loading to false (will work if Firebase auth has also initialized)
      serverSessionCheckCompletedRef.current = true;
      trySetLoadingFalse();
    }
  }, [serverUser]);

  const initializeAuth = () => {
    setIsInitialized(true);
    setLoading(true);
  };

  const logout = async (options?: { global?: boolean }) => {
    const logoutTimestamp = new Date().toISOString();
    const url = typeof window !== 'undefined' ? window.location.href : 'server';
    const isGlobal = options?.global ?? true;

    logger.debug('🔵 [AuthContext] Logout initiated', {
      category: 'auth',
      timestamp: logoutTimestamp,
      url,
      userAgent: typeof window !== 'undefined' ? navigator.userAgent : 'server',
      userId: user?.uid || serverUser?.userId || 'unknown',
      userEmail: user?.email || serverUser?.email || 'unknown',
      scope: isGlobal ? 'GLOBAL' : 'LOCAL',
      stackTrace: new Error().stack,
    });

    try {
      // Always attempt server logout
      // We wrap this in a try/catch so client-side logout happens regardless of server status
      try {
        logger.debug(`🟢 [AuthContext] Sending ${isGlobal ? 'GLOBAL' : 'LOCAL'} logout request to server`, {
          category: 'auth',
          timestamp: new Date().toISOString(),
        });

        const logoutResponse = await csrfMutatingFetch('/api/auth/logout', {
          method: 'POST',
          body: JSON.stringify({ scope: isGlobal ? 'global' : 'local' }),
        });

        logger.debug('🟢 [AuthContext] Logout response received', {
          category: 'auth',
          status: logoutResponse.status,
          statusText: logoutResponse.statusText,
          ok: logoutResponse.ok,
          timestamp: new Date().toISOString(),
        });

        if (!logoutResponse.ok) {
          const errorText = await logoutResponse.text();
          logger.warn('⚠️ [AuthContext] Server logout returned non-OK status', {
            category: 'auth',
            status: logoutResponse.status,
            statusText: logoutResponse.statusText,
            errorText,
          });
        }
      } catch (serverError) {
        logger.warn('⚠️ [AuthContext] Server logout failed, proceeding with client logout', {
          category: 'auth',
          error: serverError instanceof Error ? serverError.message : String(serverError),
          stack: serverError instanceof Error ? serverError.stack : undefined,
        });
      } finally {
        clearCsrfTokenCache();
      }

      // Sign out from Firebase client
      if (auth) {
        await signOut(auth);
      }
      clearPendingPurchasePriceId();

      // Reset state
      setServerUser(null);
      setDecisionJson(null);
      setIsInitialized(false);
      setUserContext(null);
      setScopedDb(null);
      setUser(null);
      // Clear local storage items that might contain sensitive data
      if (typeof window !== 'undefined') {
        localStorage.removeItem('selectedClient');
        // Clear session correlation ID
        const { clearCorrelationId } = await import('@/lib/logging');
        clearCorrelationId();
      }
    } catch (error) {
      logger.error('Logout error:', error);
      throw error;
    }
  };



  const isGuest = serverUser?.isGuest || userContext?.isGuest || false;
  const userId = serverUser?.userId || userContext?.userId || null;
  const isAuthenticated = !!serverUser;

  const refreshServerSession = useCallback(async (): Promise<ServerUser | null> => {
    try {
      const response = await fetchSessionStatus();
      if (!response.ok) return null;

      const data = await response.json();
      if (data.authenticated && data.user) {
        setServerUser(data.user);
        setDecisionJson(decisionJsonFromSession(data));
        setIsInitialized(true);
        return data.user as ServerUser;
      }
      return null;
    } catch (error) {
      logger.debug('[AuthContext] refreshServerSession failed', error);
      return null;
    }
  }, []);

  useEffect(() => {
    if (serverUser?.roles) {
      previousRolesRef.current = serverUser.roles;
    }
  }, [serverUser?.userId, serverUser?.roles]);

  const sessionRecoveryRef = useRef<() => ReturnType<typeof recoverExpiredSession>>(
    async () => 'unavailable',
  );
  sessionRecoveryRef.current = () => {
    const firebaseUser = auth?.currentUser ?? null;
    return recoverExpiredSession({
      getIdToken: firebaseUser ? () => getIdToken(firebaseUser, true) : null,
      completeLogin,
      onReminted: (body) => {
        const data = body as { user?: ServerUser };
        if (!data.user) return;
        setServerUser(data.user);
        setDecisionJson(decisionJsonFromSession(data as Record<string, unknown>));
        setIsInitialized(true);
      },
      onStaleSignIn: async () => {
        rememberStaleSessionOnLogin();
        try {
          await logout();
        } catch (signOutError) {
          logger.warn('[AuthContext] Sign-out after expired session failed:', signOutError);
        }
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          window.location.assign('/login');
        }
      },
      onDenied: async (path) => {
        try {
          await logout();
        } catch (signOutError) {
          logger.warn('[AuthContext] Sign-out after denied session remint failed:', signOutError);
        }
        if (typeof window !== 'undefined' && window.location.pathname !== path) {
          window.location.assign(path);
        }
      },
    });
  };

  useEffect(() => registerSessionRecovery(() => sessionRecoveryRef.current()), []);

  // Re-fetch session on focus/route change to detect mid-session deactivation
  useEffect(() => {
    if (loading || !serverUser || typeof window === 'undefined') {
      return;
    }

    const refreshAccessOnFocusOrRoute = async () => {
      const priorRoles = previousRolesRef.current ?? serverUser.roles ?? [];
      try {
        const response = await fetchSessionStatus();
        if (!response.ok) {
          if (response.status === 401) {
            const body = await response.json().catch(() => null);
            if (isInvalidCredentialFailure(response.status, body)) {
              await requestSessionRecovery();
            }
          }
          return;
        }

        const data = await response.json();
        const session = normalizeSessionResponse(data);
        if (!session.authenticated || !session.user) {
          if (priorRoles.length > 0) {
            // Only an explicit server reason may be presented as an admin action (AUD-06).
            const ended = describeSessionEnded(session.endedReason);
            const { toast } = await import('@/hooks/use-toast');
            toast({
              title: ended.title,
              description: ended.description,
              variant: ended.adminAction ? 'destructive' : 'default',
            });
            await logout();
          }
          return;
        }

        const newRoles: string[] = (session.user as { roles?: string[] }).roles ?? [];
        previousRolesRef.current = newRoles;
        applySessionPayload(data);
        await maybeRemintFromExpiresSoon(data, user);

        // AUD-07: never infer revocation from an empty roles array. A genuine
        // empty set is served as authenticated:true with roles:[]; a load
        // failure is 503 and already returned above. Route gates decide access.
      } catch (error) {
        logger.debug('[AuthContext] Mid-session access refresh failed', error);
      }
    };

    let lastVisibilityRefresh = 0;
    const VISIBILITY_REFRESH_MS = 30_000;

    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastVisibilityRefresh < VISIBILITY_REFRESH_MS) return;
      lastVisibilityRefresh = now;
      void refreshAccessOnFocusOrRoute();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [loading, serverUser, logout, user, applySessionPayload, maybeRemintFromExpiresSoon]);

  // Debug logging for auth state
  useEffect(() => {
    logger.debug('[AuthContext] Auth state updated:', {
      isAuthenticated,
      loading,
      hasServerUser: !!serverUser,
      hasUser: !!user,
      serverUserEmail: serverUser?.email,
      userEmail: user?.email
    });
  }, [isAuthenticated, loading, serverUser, user]);

  return (
    <AuthContext.Provider value={{
      user,
      serverUser,
      decisionJson,
      loading,
      logout,

      initializeAuth,
      isGuest,
      userId,
      userContext,
      scopedDb,
      isInitialized,
      isAuthenticated,
      refreshServerSession,
    }}>
      {children}
      <ZombieSessionDetector />
    </AuthContext.Provider>
  );
}

/**
 * Internal component to detect and fix "zombie" authentication states.
 * A zombie state occurs when the client-side Firebase auth and server-side session cookie are inconsistent.
 */
function ZombieSessionDetector() {
  const { user, serverUser, loading, logout } = useAuth();
  const firebaseAuthInitializedRef = useRef(false);
  const serverSessionCheckCompletedRef = useRef(false);
  const logoutInProgressRef = useRef(false);

  // We need to track the internal state of the parent AuthProvider
  // since we don't have direct access to its refs here.
  // We'll use the loading state as a proxy, but we also want to be sure
  // that the initial checks have actually finished.
  useEffect(() => {
    if (!loading) {
      if (user !== undefined) firebaseAuthInitializedRef.current = true;
      serverSessionCheckCompletedRef.current = true;
    }
  }, [loading, user]);


  useEffect(() => {
    // Only run this check after both Firebase and server session checks are complete
    if (loading || !serverSessionCheckCompletedRef.current || logoutInProgressRef.current) {
      return;
    }

    // CASE: Server session exists but Firebase user is null
    // This is the classic "zombie" state described by the user.
    // The UI says "Not signed in" because user is null, but the server thinks it is authenticated.
    // This prevents clean sign-ins and can cause buggy "guest mode" behavior.
    if (serverUser && !user) {
      logger.warn('🧟 [AuthContext] Zombie session detected: serverUser exists but Firebase user is null. Attempting to restore session...', {
        category: 'auth',
        serverUserId: serverUser.userId,
        serverUserEmail: serverUser.email,
        firebaseUser: null,
        url: typeof window !== 'undefined' ? window.location.href : 'server',
        timestamp: new Date().toISOString(),
      });

      const restoreSession = async () => {
        try {
          // Attempt to get a custom token from the server using the valid session cookie
          const response = await fetch('/api/auth/token');

          if (response.ok) {
            const data = await response.json();
            if (data.token) {
              logger.debug('✅ [AuthContext] Received custom token for session restoration');

              const { signInWithCustomToken } = await import('firebase/auth');
              if (auth) {
                await signInWithCustomToken(auth, data.token);
                logger.debug('✅ [AuthContext] Successfully restored Firebase Auth session from server cookie');
                return;
              }
            }
          }

          throw new Error('Failed to obtain or use custom token');
        } catch (restoreError) {
          logger.error('❌ [AuthContext] Session restoration failed, proceeding with logout:', restoreError);

          // Fallback to logout if restoration fails
          try {
            logoutInProgressRef.current = true;
            await logout();
            logger.debug('✅ [AuthContext] Zombie session cleared (fallback)');
          } catch (error) {
            logger.error('❌ [AuthContext] Failed to clear zombie session:', error);
          } finally {
            logoutInProgressRef.current = false;
          }
        }
      };

      restoreSession();
    }

    // CASE: Mismatched users (Uid mismatch)
    // If the server thinks we are one user and Firebase thinks another,
    // clear everything to be safe.
    if (serverUser && user && serverUser.userId !== user.uid) {
      // Small optimization: If we just restored the session (serverUser matches user.uid), 
      // this block won't run.
      // But if there's a genuine mismatch, we logout.

      logger.warn('🧟 [AuthContext] Mismatched session detected. Clearing all sessions to force re-login...', {
        category: 'auth',
        serverUid: serverUser.userId,
        serverUserEmail: serverUser.email,
        firebaseUid: user.uid,
        firebaseEmail: user.email,
        url: typeof window !== 'undefined' ? window.location.href : 'server',
        timestamp: new Date().toISOString(),
        stackTrace: new Error().stack,
      });

      const performLogout = async () => {
        try {
          logoutInProgressRef.current = true;
          await logout();
          logger.debug('✅ [AuthContext] Mismatched session cleared successfully');
        } catch (error) {
          logger.error('❌ [AuthContext] Failed to clear mismatched session:', error);
        } finally {
          logoutInProgressRef.current = false;
        }
      };

      performLogout();
    }
  }, [user, serverUser, loading, logout]);

  return null;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Helper hook to get scoped database directly
// Returns null if not authenticated or still loading
export function useScopedDb(): ScopedDatabase | null {
  const { scopedDb } = useAuth();
  return scopedDb;
}
